import React, { useState } from 'react';
import { themeKey } from './useThemes';
import { useT } from '../../i18n';

// Add and delete custom themes.
//
// "Not assigned" is not listed: it is the absence of a theme, not one, and so
// cannot be deleted. Deleting a theme unassigns every asset that carried it —
// the server does that in the same statement — so the confirmation says how
// many assets that is before it happens.

export function ThemeManager({ themes: ctl }) {
    const { t, tn } = useT();
    const [name, setName] = useState('');
    const [busy, setBusy] = useState(false);

    const add = async (e) => {
        e.preventDefault();
        const trimmed = name.trim();
        if (!trimmed) return;
        setBusy(true);
        if (await ctl.create(trimmed)) setName('');
        setBusy(false);
    };

    const remove = async (theme) => {
        const count = ctl.counts.get(theme.id) ?? 0;
        const message = count
            ? tn('themes.confirmDeleteAssigned', count, { name: theme.name })
            : t('themes.confirmDelete', { name: theme.name });
        if (!window.confirm(message)) return;
        setBusy(true);
        await ctl.remove(theme.id);
        setBusy(false);
    };

    return (
        <div className="themes__panel">
            {ctl.themes.length === 0 ? (
                <p className="themes__empty">{t('themes.empty')}</p>
            ) : (
                <ul className="themes__list">
                    {ctl.themes.map((theme) => (
                        <li key={theme.id} className="themes__chip">
                            <span>{theme.name}</span>
                            <span className="themes__count">{ctl.counts.get(theme.id) ?? 0}</span>
                            <button
                                type="button"
                                className="themes__remove"
                                disabled={busy}
                                onClick={() => remove(theme)}
                                aria-label={t('themes.deleteAria', { name: theme.name })}
                                title={t('themes.deleteAria', { name: theme.name })}
                            >
                                ×
                            </button>
                        </li>
                    ))}
                </ul>
            )}
            <form className="themes__add" onSubmit={add}>
                <input
                    type="text"
                    className="positions__search"
                    placeholder={t('themes.newPlaceholder')}
                    value={name}
                    maxLength={60}
                    onChange={(e) => setName(e.target.value)}
                    aria-label={t('themes.newAria')}
                />
                <button type="submit" className="positions__filter" disabled={busy || !name.trim()}>
                    {t('themes.add')}
                </button>
            </form>
            {ctl.error && <p className="holdings__error" role="alert">{t(ctl.error)}</p>}
        </div>
    );
}

/** The Theme cell: a dropdown, so assigning is one action from the row. */
export function ThemeSelect({ themes: ctl, symbol }) {
    const { t } = useT();
    const current = ctl.themeOf(symbol);
    return (
        <select
            className={`themes__select${current ? '' : ' is-unassigned'}`}
            value={current?.id ?? ''}
            onChange={(e) => ctl.assign(themeKey(symbol), e.target.value || null)}
            aria-label={t('themes.assignAria', { symbol })}
        >
            <option value="">{t('themes.notAssigned')}</option>
            {ctl.themes.map((theme) => (
                <option key={theme.id} value={theme.id}>{theme.name}</option>
            ))}
        </select>
    );
}

/** The button that opens the manager, for a table's control row. */
export function ThemeManagerToggle({ open, onToggle }) {
    const { t } = useT();
    return (
        <button
            type="button"
            className={`positions__filter${open ? ' is-active' : ''}`}
            aria-expanded={open}
            onClick={onToggle}
        >
            {t('themes.manage')}
        </button>
    );
}
